import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Role } from '@/lib/types/api'
import type {
  EntraTrustedGroupMapping,
  EntraTrustedGroupMappingInput,
} from '@/lib/services/integrationService'

const formSchema = z.object({
  name: z.string().min(2).max(200),
  groupObjectId: z.string().min(2).max(255),
  roleSlug: z.string().min(1).max(100),
  description: z.string().max(1000).nullable().optional(),
  isActive: z.boolean(),
})

type FormValues = z.infer<typeof formSchema>

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (payload: EntraTrustedGroupMappingInput) => Promise<void>
  mapping?: EntraTrustedGroupMapping | null
  roles: Role[]
  mode: 'create' | 'edit'
}

export function EntraTrustedGroupMappingDialog({
  open,
  onOpenChange,
  onSubmit,
  mapping,
  roles,
  mode,
}: Props) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      groupObjectId: '',
      roleSlug: '',
      description: '',
      isActive: true,
    },
  })

  useEffect(() => {
    if (!open) return

    if (mapping && mode === 'edit') {
      form.reset({
        name: mapping.name,
        groupObjectId: mapping.groupObjectId,
        roleSlug: mapping.roleSlug,
        description: mapping.description ?? '',
        isActive: mapping.isActive,
      })
      return
    }

    form.reset({
      name: '',
      groupObjectId: '',
      roleSlug: roles[0]?.slug ?? '',
      description: '',
      isActive: true,
    })
  }, [form, mapping, mode, open, roles])

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit({
        name: values.name,
        groupObjectId: values.groupObjectId,
        roleSlug: values.roleSlug,
        description: values.description || null,
        isActive: values.isActive,
      })
      onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? t('integration.entra.addTrustedGroup', 'Vertrouwde groep toevoegen')
              : t('integration.entra.editTrustedGroup', 'Vertrouwde groep bewerken')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'integration.entra.groupDialogDescription',
              'Gebruik Entra group Object IDs. Weergavenamen zijn niet betrouwbaar voor autorisatie.'
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('integration.entra.mappingName', 'Naam')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        'integration.entra.mappingNamePlaceholder',
                        'VRH-AI-Crisiscommunicatietool-Viewer'
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="groupObjectId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('integration.entra.groupObjectId', 'Group Object ID')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="00000000-0000-0000-0000-000000000000"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'integration.entra.groupObjectIdHelp',
                      'Voer exacte Entra Object ID in uit groups-claim of Azure Portal.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="roleSlug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('integration.entra.internalRole', 'Interne rol')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('integration.entra.selectRole', 'Selecteer rol')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {roles.map(role => (
                        <SelectItem key={role.id} value={role.slug}>
                          {role.name} ({role.slug})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.description', 'Beschrijving')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-md border p-4">
                  <div className="space-y-1">
                    <FormLabel>{t('integration.entra.enabled', 'Ingeschakeld')}</FormLabel>
                    <FormDescription>
                      {t(
                        'integration.entra.enabledHelp',
                        'Alleen ingeschakelde mappings kunnen toegang verlenen.'
                      )}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel', 'Annuleren')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {mode === 'create'
                  ? t('integration.entra.createMapping', 'Toevoegen')
                  : t('integration.entra.saveMapping', 'Opslaan')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
